import { motion } from 'framer-motion'
import { useChatStore, type ChatMessage } from '../../stores/chatStore'

interface SidebarProps {
  theme: 'dark' | 'light'
  onToggleTheme: () => void
  onClose: () => void
}

export default function Sidebar({ theme, onToggleTheme, onClose }: SidebarProps) {
  const { sessions, currentSessionId, createSession, switchSession, clearMessages, goToWelcome } = useChatStore()

  return (
    <div className="h-full flex flex-col w-[260px]">
      {/* Header */}
      <div className="p-4 flex items-center justify-between border-b border-[var(--border-subtle)]">
        <button
          onClick={() => {
            goToWelcome()
            window.scrollTo(0, 0)
          }}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <div className="w-7 h-7 rounded bg-[var(--accent-teal)] flex items-center justify-center">
            <span className="font-mono font-bold text-xs text-[var(--bg-primary)]">8K</span>
          </div>
          <span className="font-display font-semibold text-sm">Agent Connect</span>
        </button>
        <button
          onClick={onClose}
          className="p-1.5 rounded hover:bg-[var(--bg-tertiary)] transition-colors"
        >
          <ChevronLeftIcon className="w-4 h-4 text-[var(--text-muted)]" />
        </button>
      </div>

      {/* New Chat Button */}
      <div className="p-3">
        <button
          onClick={() => {
            createSession()
          }}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--border-default)] hover:bg-[var(--bg-tertiary)] transition-colors text-sm font-mono"
        >
          <PlusIcon className="w-4 h-4" />
          New search
        </button>
      </div>

      {/* Sessions List */}
      <div className="flex-1 overflow-y-auto px-3 pb-3">
        <div className="text-xs font-mono text-[var(--text-muted)] uppercase tracking-wide px-2 py-2">
          Recent Sessions
        </div>
        <div className="space-y-1">
          {sessions.map((session) => (
            <SessionItem
              key={session.id}
              session={session}
              isActive={session.id === currentSessionId}
              onClick={() => switchSession(session.id)}
            />
          ))}
          {sessions.length === 0 && (
            <p className="text-xs text-[var(--text-muted)] px-2 py-4">
              No sessions yet. Start a search!
            </p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-[var(--border-subtle)]">
        <div className="flex items-center justify-between">
          <button
            onClick={onToggleTheme}
            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors text-sm font-mono text-[var(--text-muted)]"
          >
            {theme === 'dark' ? (
              <>
                <SunIcon className="w-4 h-4" />
                Light
              </>
            ) : (
              <>
                <MoonIcon className="w-4 h-4" />
                Dark
              </>
            )}
          </button>
          <button
            onClick={clearMessages}
            className="p-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors"
            title="Clear current session"
          >
            <TrashIcon className="w-4 h-4 text-[var(--text-muted)]" />
          </button>
        </div>
      </div>
    </div>
  )
}

interface SessionItemProps {
  session: { id: string; title: string; messages: ChatMessage[]; createdAt: Date }
  isActive: boolean
  onClick: () => void
}

function SessionItem({ session, isActive, onClick }: SessionItemProps) {
  const preview = session.messages[0]?.content || 'New session'

  return (
    <motion.button
      onClick={onClick}
      className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
        isActive
          ? 'bg-[var(--bg-tertiary)] text-[var(--text-primary)]'
          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]'
      }`}
      whileHover={{ x: 2 }}
    >
      <div className="text-sm font-mono truncate">{session.title || preview}</div>
      <div className="text-xs text-[var(--text-muted)] mt-0.5">
        {session.messages.length} messages
      </div>
    </motion.button>
  )
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  )
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  )
}

function SunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  )
}

function MoonIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
    </svg>
  )
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  )
}

